import { useEffect, useState } from "react";
import { Table, Tag, Modal, message, Spin } from "antd";
import { ColumnsType } from "antd/es/table";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface PipelineRun {
  id: number;
  pipeline_uuid: string | null;
  block_uuid: string | null;
  status: string;
  rows_extracted: number;
  rows_inserted: number;
  rows_updated: number;
  rows_deleted: number;
  rows_staged: number;
  reconciliation_notes: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
}

const statusColorMap: Record<string, string> = {
  completed: "green",
  success: "green",
  completed_with_warnings: "orange",
  running: "blue",
  failed: "red",
  error: "red",
  pending: "orange",
};

const DataSync: React.FC = () => {
  const [data, setData] = useState<PipelineRun[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [reconciliationDetails, setReconciliationDetails] = useState<any[]>([]);
  const [modalTitle, setModalTitle] = useState("");
  const [t] = useTranslation("common");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios
        .get("/dashboard/datasync-summary")
        .then((res) => res.data);
      setData(res);
    } catch (err) {
      console.log(err);
      message.error(t("Something went wrong. Please try again later."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getRowClassName = (record: PipelineRun) => {
    const status = record.status?.toLowerCase();
    if (status === "failed" || status === "error") {
      return "bg-red-200";
    }
    return "";
  };

  const openReconciliationDetails = async (record: PipelineRun) => {
    setModalTitle(`Reconciliation Details — Run #${record.id}`);
    setModalOpen(true);
    setModalLoading(true);
    try {
      const res = await axios
        .get(`/dashboard/reconciliation-details/${record.id}`)
        .then((res) => res.data);
      setReconciliationDetails(res);
    } catch (err) {
      console.log(err);
      message.error(t("Something went wrong. Please try again later."));
      setReconciliationDetails([]);
    } finally {
      setModalLoading(false);
    }
  };

  const detailColumns: ColumnsType<any> =
    reconciliationDetails.length > 0
      ? Object.keys(reconciliationDetails[0]).map((key) => ({
          title: key,
          dataIndex: key,
          key,
          render: (val: any) =>
            val == null ? "—" : typeof val === "object" ? JSON.stringify(val) : String(val),
        }))
      : [];

  const columns: ColumnsType<PipelineRun> = [
    {
      title: t("dashboard.table.status"),
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (status: string) => {
        const color = statusColorMap[status?.toLowerCase()] || "default";
        return <Tag color={color}>{status}</Tag>;
      },
      filters: [
        { text: "completed", value: "completed" },
        { text: "completed_with_warnings", value: "completed_with_warnings" },
        { text: "failed", value: "failed" },
        { text: "running", value: "running" },
      ],
      onFilter: (value, record) =>
        record.status?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: t("dashboard.table.pipeline"),
      dataIndex: "pipeline_uuid",
      key: "pipeline_uuid",
      ellipsis: true,
    },
    {
      title: t("dashboard.table.block"),
      dataIndex: "block_uuid",
      key: "block_uuid",
      ellipsis: true,
    },
    {
      title: t("dashboard.table.startedAt"),
      dataIndex: "started_at",
      key: "started_at",
      render: (date: string | null) =>
        date ? moment.utc(date).format("YYYY-MM-DD HH:mm:ss") : t("dashboard.table.N/A"),
      sorter: (a, b) => {
        const dateA = a.started_at ? moment(a.started_at).valueOf() : 0;
        const dateB = b.started_at ? moment(b.started_at).valueOf() : 0;
        return dateA - dateB;
      },
      defaultSortOrder: "descend",
    },
    {
      title: t("dashboard.table.duration"),
      dataIndex: "duration_seconds",
      key: "duration_seconds",
      width: 100,
      render: (val: number | null) => (val != null ? `${val}s` : t("dashboard.table.N/A")),
      sorter: (a, b) => (a.duration_seconds ?? 0) - (b.duration_seconds ?? 0),
    },
    {
      title: t("dashboard.table.extracted"),
      dataIndex: "rows_extracted",
      key: "rows_extracted",
      width: 90,
      sorter: (a, b) => a.rows_extracted - b.rows_extracted,
    },
    {
      title: t("dashboard.table.inserted"),
      dataIndex: "rows_inserted",
      key: "rows_inserted",
      width: 90,
      sorter: (a, b) => a.rows_inserted - b.rows_inserted,
    },
    {
      title: t("dashboard.table.updated"),
      dataIndex: "rows_updated",
      key: "rows_updated",
      width: 90,
      sorter: (a, b) => a.rows_updated - b.rows_updated,
    },
    {
      title: t("dashboard.table.deleted"),
      dataIndex: "rows_deleted",
      key: "rows_deleted",
      width: 90,
      sorter: (a, b) => a.rows_deleted - b.rows_deleted,
    },
    {
      title: t("dashboard.table.notes"),
      dataIndex: "reconciliation_notes",
      key: "reconciliation_notes",
      width: 250,
      render: (notes: string | null, record: PipelineRun) => {
        if (!notes) return "—";
        return (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              cursor: "pointer",
              color: "#1890ff",
              textDecoration: "underline",
            }}
            onClick={() => openReconciliationDetails(record)}
          >
            {notes}
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full">
      <div className="max-w-full overflow-auto">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowClassName={getRowClassName}
          className="border flex-grow"
          pagination={false}
          size="small"
        />
      </div>

      <Modal
        title={modalTitle}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={900}
      >
        {modalLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : reconciliationDetails.length > 0 ? (
          <Table
            rowKey={(_, index) => String(index)}
            columns={detailColumns}
            dataSource={reconciliationDetails}
            pagination={false}
            size="small"
            scroll={{ x: true }}
          />
        ) : (
          <p>No reconciliation details found.</p>
        )}
      </Modal>
    </div>
  );
};

export default DataSync;
